import { n as DEFAULT_ARCHIVE_LIMITS, t as ArchiveLimitError } from "./archive-limits-CMo2zcn-.js";
import { t as byteCountingStream } from "./byte-counting-stream-hK69xe4i.js";
import { isDefinitivelyGone, parseContentRangeTotal } from "./storage/range-probe.js";
import { StructuralReadError } from "./storage/structural-read-error.js";
//#region ../src/storage/capped-response-body.ts
/**
* A response body read whole, but never past a byte cap - the transport
* half of the archive caps (`archive-limits.ts`).
*
* Two checks, because neither alone is enough:
* - BEFORE reading: an announced `Content-Length` above the cap refuses
*   the body without fetching it (the stream is cancelled, so the bytes
*   never cross the wire beyond what the network already buffered);
* - WHILE reading: the stream is counted chunk by chunk and cancelled the
*   moment it passes the cap, because the header may be missing (chunked
*   transfer) or lie.
*
* The count is a stream (`byte-counting-stream.ts`) feeding
* `Response.blob()`, so no chunk is collected in page memory on the way
* (K0 milestone review R5): the cap bounds the download, and the browser
* assembles the Blob outside the JS heap.
*/
function announcedLength(headers) {
	const raw = headers.get("content-length");
	if (raw === null || raw.trim() === "") return null;
	const value = Number(raw);
	return Number.isSafeInteger(value) && value >= 0 ? value : null;
}
/** `blob` with the response's type: `Response.blob()` types it from its
*  own (absent) headers. A Blob over a Blob is a reference, not a copy. */
function typed(blob, type) {
	return type === "" || blob.type === type ? blob : new Blob([blob], { type });
}
function refuse(maxBytes, observed) {
	throw new ArchiveLimitError("archive-bytes", maxBytes, observed);
}
/**
* The whole body as a Blob (typed by the response's `content-type`), or
* an {@link ArchiveLimitError} of kind `archive-bytes` once it is, or
* announces to be, larger than `maxBytes`.
*
* @throws RangeError for a cap that is not a positive safe integer.
*/
async function readResponseBodyCapped(res, maxBytes) {
	if (!Number.isSafeInteger(maxBytes) || maxBytes <= 0) throw new RangeError(`readResponseBodyCapped: cap must be a positive integer, got ${String(maxBytes)}`);
	const announced = announcedLength(res.headers);
	if (announced !== null && announced > maxBytes) {
		await res.body?.cancel().catch(() => void 0);
		refuse(maxBytes, announced);
	}
	const type = res.headers.get("content-type") ?? "";
	if (!res.body) {
		const blob = await res.blob();
		return blob.size > maxBytes ? refuse(maxBytes, blob.size) : blob;
	}
	const counted = res.body.pipeThrough(byteCountingStream((total) => {
		if (total > maxBytes) refuse(maxBytes, total);
	}));
	return typed(await new Response(counted).blob(), type);
}
//#endregion
//#region ../src/storage/remote-range-byte-source.ts
/**
* HTTP Range reads straight against a hosted archive, plus the opening probe.
*
* The probe issues a HEAD (total size from the CORS-safelisted
* `Content-Length`, which every provider exposes — unlike `Content-Range`)
* and a `Range: bytes=0-0` GET (support detection). A `fetch` rejection here
* — a CORS block or a dropped connection — propagates so the caller can map
* it to a fatal error: a CORS-blocked link is unrecoverable, since it defeats
* both the range path and the full-body fallback.
*/
/**
* The one structurally-recoverable read failure: the host answered a range
* read with 200 (ignored `Range` mid-session — CDN node variance, a backend
* flip). Distinguishable from other `StructuralReadError`s so the
* orchestrator can swap the session onto a full local copy instead of
* failing it (`open-remote-archive.ts`).
*/
var RangeIgnoredError = class extends StructuralReadError {
	name = "RangeIgnoredError";
};
/** A header value as archive size: finite safe non-negative integer, or null. */
function parseArchiveSize(header) {
	if (header === null || header.trim() === "") return null;
	const value = Number(header);
	return Number.isSafeInteger(value) && value >= 0 ? value : null;
}
/**
* Why a range-read response is unusable, or null when it is good. A 200 means
* the host ignored `Range` and streamed the full file — returning that body as
* the slice would silently corrupt every downstream parse, so it throws the
* distinguishable `RangeIgnoredError`, which the orchestrator
* (`open-remote-archive.ts`) recovers from by swapping the session onto a
* full local copy. A bare `RemoteRangeByteSource` consumer sees the error
* as permanent. 4xx (expired signed
* link, file gone, bad range) is likewise permanent; anything else non-206 is
* transient. A readable `Content-Range` naming different offsets means the
* server answered a different slice — but the header is not CORS-safelisted
* (e.g. raw.githubusercontent exposes no headers), so null is normal and the
* caller's body-length check stays the always-on guard.
*/
function classifyRangeResponse(status, contentRange, offset, end) {
	if (status === 200) return new RangeIgnoredError(`host ignored Range (200) at ${offset}-${end} — fall back to a full download`);
	if (status !== 206) {
		const message = `range read failed (${status}) at ${offset}-${end}`;
		return status >= 400 && status < 500 ? new StructuralReadError(message) : new Error(message);
	}
	if (contentRange !== null) {
		const m = /^bytes\s+(\d+)-(\d+)\//.exec(contentRange.trim());
		if (m === null || Number(m[1]) !== offset || Number(m[2]) !== end) return new StructuralReadError(`server answered range ${contentRange} instead of ${offset}-${end}`);
	}
	return null;
}
/**
* The 206 body of a read of `length` bytes - never more (K0 milestone
* review R2). Read chunk by chunk into one buffer of exactly `length`; a
* chunk that would overflow it cancels the rest of the body and fails the
* read, so a host answering a small range with gigabytes cannot get past
* the transport cap this way. A body with no stream (Response-shaped
* fakes) is read whole and length-checked, as before.
*/
async function readExactBody(res, length, where) {
	const wrongLength = (got) => new StructuralReadError(`range read at ${where} returned ${got} bytes, expected ${String(length)}`);
	if (!res.body) {
		const bytes = new Uint8Array(await res.arrayBuffer());
		if (bytes.length !== length) throw wrongLength(String(bytes.length));
		return bytes;
	}
	const out = new Uint8Array(length);
	const reader = res.body.getReader();
	let filled = 0;
	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		if (filled + value.byteLength > length) {
			await reader.cancel().catch(() => void 0);
			throw wrongLength(`more than ${String(length)}`);
		}
		out.set(value, filled);
		filled += value.byteLength;
	}
	if (filled !== length) throw wrongLength(String(filled));
	return out;
}
/** A HEAD is headers-only — anything slower than this is a hung connection. */
const HEAD_TIMEOUT_MS = 15e3;
/** The probe GET may legitimately stream the whole archive (a 200 from a
*  range-refusing host becomes the eager-local body), so its budget must cover
*  a full download on a slow cellular link, not just headers. */
const PROBE_GET_TIMEOUT_MS = 3e5;
/** A range read returns a small slice; a stalled one must fail fast so a
*  caller that chooses to retry gets the chance instead of hanging forever
*  on a dead connection. (Nothing retries automatically today — the
*  orchestrator adds recovery only for the range-ignored 200 case; PR #357
*  review.) */
const RANGE_READ_TIMEOUT_MS = 2e4;
/** Freshness validators readable off a response, where CORS lets us. */
function readValidators(headers) {
	const etag = headers.get("etag");
	const lastModified = headers.get("last-modified");
	if (etag === null && lastModified === null) return void 0;
	return {
		...etag !== null ? { etag } : {},
		...lastModified !== null ? { lastModified } : {}
	};
}
/**
* Size + freshness validators via a single HEAD, or null when the HEAD fails
* or is refused — the lightweight check a cache-revalidating caller runs
* before deciding whether a full probe is needed at all.
*/
async function fetchRemoteValidators(url, fetchImpl) {
	try {
		const head = await fetchImpl(url, {
			method: "HEAD",
			cache: "no-cache",
			signal: AbortSignal.timeout(HEAD_TIMEOUT_MS)
		});
		if (isDefinitivelyGone(head.status)) return { kind: "missing" };
		if (!head.ok) return null;
		const validators = readValidators(head.headers);
		return {
			kind: "ok",
			size: parseArchiveSize(head.headers.get("content-length")),
			...validators !== void 0 ? { validators } : {}
		};
	} catch {
		return null;
	}
}
/** The transport cap on a KNOWN size (null = unknown, checked later while
*  a body streams). */
function assertWithinArchiveCap(size, maxBytes) {
	if (size !== null && size > maxBytes) throw new ArchiveLimitError("archive-bytes", maxBytes, size);
}
function asOkHead(info) {
	return info?.kind === "ok" ? info : null;
}
/**
* The archive size a probe yields. On a 206, `Content-Range`'s total (when
* CORS lets us read it) recovers a size HEAD could not supply — this is what
* makes the loader work behind a CORS proxy — and when BOTH are readable
* they must agree: a host confused about its own size gets no size at all
* (→ full-download degrade) rather than a guess that zip offsets would be
* anchored to (PR #357 review).
*/
function resolveProbeSize(headSize, probe) {
	if (probe.status !== 206) return headSize;
	const total = parseContentRangeTotal(probe.headers.get("content-range"));
	if (total === null) return headSize;
	if (headSize !== null && headSize !== total) return null;
	return total;
}
/**
* HEAD for size + `bytes=0-0` GET for range support. Throws if `fetch`
* rejects, and throws {@link ArchiveLimitError} (`archive-bytes`) when the
* archive is larger than `maxArchiveBytes`: checked from the HEAD's size
* BEFORE the GET is sent, from the 206's total before any range read, and
* while a range-ignoring host's 200 body streams (K0 of the tour kit plan).
*/
async function probeRemote(url, fetchImpl, maxArchiveBytes = DEFAULT_ARCHIVE_LIMITS.maxArchiveBytes) {
	const okHead = asOkHead(await fetchRemoteValidators(url, fetchImpl));
	const headSize = okHead?.size ?? null;
	assertWithinArchiveCap(headSize, maxArchiveBytes);
	const probe = await fetchImpl(url, {
		headers: { Range: "bytes=0-0" },
		cache: "no-cache",
		signal: AbortSignal.timeout(PROBE_GET_TIMEOUT_MS)
	});
	const validators = okHead?.validators ?? readValidators(probe.headers);
	const validatorsField = validators !== void 0 ? { validators } : {};
	const size = resolveProbeSize(headSize, probe);
	if (probe.status === 200) {
		const body = new Uint8Array(await (await readResponseBodyCapped(probe, maxArchiveBytes)).arrayBuffer());
		return {
			status: 200,
			size: size ?? body.length,
			body,
			...validatorsField
		};
	}
	await probe.body?.cancel().catch(() => void 0);
	assertWithinArchiveCap(size, maxArchiveBytes);
	return {
		status: probe.status,
		size,
		...validatorsField
	};
}
var RemoteRangeByteSource = class {
	size;
	#url;
	#fetch;
	constructor(url, size, fetchImpl) {
		this.#url = url;
		this.size = size;
		this.#fetch = (input, init) => fetchImpl(input, init);
	}
	async read(offset, length) {
		if (length <= 0) return new Uint8Array(0);
		const end = offset + length - 1;
		const res = await this.#fetch(this.#url, {
			headers: { Range: `bytes=${offset}-${end}` },
			cache: "no-cache",
			signal: AbortSignal.timeout(RANGE_READ_TIMEOUT_MS)
		});
		const failure = classifyRangeResponse(res.status, res.headers.get("content-range"), offset, end);
		if (failure !== null) {
			await res.body?.cancel().catch(() => void 0);
			throw failure;
		}
		return readExactBody(res, length, `${offset}-${end}`);
	}
};
//#endregion
export { readResponseBodyCapped as a, probeRemote as i, RemoteRangeByteSource as n, fetchRemoteValidators as r, RangeIgnoredError as t };
